/**
 * Fetch token for the page proxy and rooms. The worker keeps it in an HttpOnly
 * cookie; this module only tracks its expiry, renews it before it runs out, and
 * runs Turnstile (non-interactive for almost everyone) before opening arbitrary sites.
 */

// Local dev uses Cloudflare's always-pass test key (paired with the test secret in .dev.vars).
const LOCAL = location.hostname === "localhost" || location.hostname === "127.0.0.1";
import { setProxyToken } from "../extract/walk";

const SITEKEY = LOCAL ? "1x00000000000000000000AA" : "0x4AAAAAAFQVlRb5H4yx-97w";
let exp = 0;
let tok = "";
let human = false;
let pending: Promise<void> | null = null;
let timer = 0;

async function request(ts?: string): Promise<void> {
  const res = await fetch("/api/token", {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(ts ? { ts } : {}),
  });
  if (!res.ok) {
    if (ts) throw new Error("We couldn't confirm you're a person. Reload and try again.");
    throw new Error("The game server is busy. Try again in a moment.");
  }
  const body = (await res.json()) as { exp: number; human: boolean; token: string };
  exp = body.exp * 1000;
  tok = body.token;
  setProxyToken(tok);
  human = body.human;
  schedule();
}

function schedule() {
  window.clearTimeout(timer);
  // Renew two minutes early while the tab stays open.
  timer = window.setTimeout(() => void ensureToken(true).catch(() => undefined), Math.max(30_000, exp - Date.now() - 120_000));
}

// A network switch (Wi-Fi to mobile data) can move the player to another
// network, which the token is bound to. Renew when the browser reports one.
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    if (exp) void ensureToken(true).catch(() => undefined);
  });
  const conn = (navigator as Navigator & { connection?: EventTarget }).connection;
  conn?.addEventListener?.("change", () => {
    if (exp) void ensureToken(true).catch(() => undefined);
  });
}

/** Path segment for /p/ URLs. The sandboxed page frame has an opaque origin and cannot send our cookie. */
export function proxyToken(): string {
  return tok;
}

/** A live token, renewed when it has under `margin` ms left. */
export function ensureToken(force = false, margin = 60_000): Promise<void> {
  if (!force && exp - Date.now() > margin) return Promise.resolve();
  pending ??= request().finally(() => {
    pending = null;
  });
  return pending;
}

interface TurnstileApi {
  render(el: HTMLElement, opts: Record<string, unknown>): string;
  remove(id: string): void;
}

let script: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  script ??= new Promise<TurnstileApi>((resolve, reject) => {
    const w = window as unknown as { turnstile?: TurnstileApi };
    if (w.turnstile) return resolve(w.turnstile);
    const tag = document.createElement("script");
    tag.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    tag.async = true;
    tag.onload = () => (w.turnstile ? resolve(w.turnstile) : reject(new Error("turnstile")));
    tag.onerror = () => {
      script = null;
      reject(new Error("We couldn't load the person check. Check your connection and try again."));
    };
    document.head.appendChild(tag);
  });
  return script;
}

let humanPending: Promise<void> | null = null;

/** Token with a Turnstile pass, needed before opening any site other than the demo. */
export function ensureHuman(): Promise<void> {
  if (human && exp - Date.now() > 180_000) return Promise.resolve();
  humanPending ??= (async () => {
    await ensureToken(false, 180_000);
    if (human) return;
    const api = await loadTurnstile();
    const box = document.createElement("div");
    // Only shows itself if Cloudflare needs a click (appearance: interaction-only).
    box.style.cssText = "position:fixed;left:50%;bottom:16px;transform:translateX(-50%);z-index:2147483000";
    document.body.appendChild(box);
    let id = "";
    try {
      const ts = await new Promise<string>((resolve, reject) => {
        const giveUp = window.setTimeout(() => reject(new Error("The person check took too long. Try again.")), 60_000);
        id = api.render(box, {
          sitekey: SITEKEY,
          action: "fetch",
          appearance: "interaction-only",
          callback: (t: string) => {
            window.clearTimeout(giveUp);
            resolve(t);
          },
          "error-callback": () => {
            window.clearTimeout(giveUp);
            reject(new Error("We couldn't confirm you're a person. Reload and try again."));
          },
        });
      });
      await request(ts);
    } finally {
      if (id) api.remove(id);
      box.remove();
    }
  })().finally(() => {
    humanPending = null;
  });
  return humanPending;
}

/** Retry once after renewing, for a token that went stale or an IP change. */
export async function retryForbidden(res: Response, again: () => Promise<Response>): Promise<Response> {
  if (res.status !== 403) return res;
  exp = 0;
  human = false;
  return again();
}

export function hasHuman(): boolean {
  return human && exp - Date.now() > 60_000;
}
